import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle, RotateCcw } from 'lucide-react';
import { t } from '@/lib/i18n';

interface Props {
    children?: ReactNode;
    fallback?: ReactNode;
    /** Override title (for cases where i18n isn't available) */
    errorTitle?: string;
    /** Override message */
    errorMessage?: string;
    /** Override retry label */
    retryLabel?: string;
}

interface State {
    hasError: boolean;
    error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
    }

    public render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Use props if provided, otherwise fall back to i18n
            const title = this.props.errorTitle || t('common.errorTitle');
            const message = this.props.errorMessage || t('common.errorMessage');
            const retryLabel = this.props.retryLabel || t('common.retry');

            return (
                <div className="flex flex-col items-center justify-center min-h-[400px] p-6 text-center bg-red-50 dark:bg-red-950/20 rounded-2xl border border-red-100 dark:border-red-900/30 m-4">
                    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mb-4">
                        <AlertCircle className="w-8 h-8" />
                    </div>
                    <h2 className="text-xl font-bold text-foreground mb-2">{title}</h2>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                        {message}
                    </p>
                    <Button
                        onClick={() => this.setState({ hasError: false })}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
                    >
                        <RotateCcw className="w-4 h-4" />
                        {retryLabel}
                    </Button>
                </div>
            );
        }

        return this.props.children;
    }
}
